<template>
  <DeepDialog
    v-model="showDialog"
    fullscreen-on-mobile
    :title="i18n.t('titles.observers')"
    width="600"
    @keydown.esc="closeDialog"
  >
    <template #content>
      <div class="text-black mb-4 text-body-large">
        {{ i18n.t('texts.observers_dialog_hint') }}
      </div>
      <v-form
        ref="observersForm"
        @keydown.enter.prevent
        @submit.prevent="updateObservers"
      >
        <ObserverInput
          v-for="(observer, index) in observers"
          :key="observer.key"
          :index="index"
          :observer="observer"
          :observers="observers"
          @add-group-observers="addGroupObservers"
          @remove-observer="removeObserver(index)"
          @update-observer="updateObserver"
        />
      </v-form>
      <div class="mt-4">
        <VBtnTertiary
          :aria-label="i18n.t('buttons.add_observer')"
          class="deep-text-button pl-0"
          data-test-id="observer-add"
          size="small"
          tabindex="0"
          variant="plain"
          @click="addNewObserver"
          @keyup.enter="addNewObserver"
        >
          {{ i18n.t('buttons.add_observer') }}
        </VBtnTertiary>
      </div>
    </template>
    <template #actions>
      <VBtnSecondary
        data-test-id="dialog-cancel-button"
        :text="i18n.t('buttons.cancel')"
        variant="outlined"
        @click="closeDialog"
      />
      <VBtnPrimary
        color="primary"
        data-test-id="save-observers"
        :disabled="!isFormValid"
        :loading="inProgress"
        :text="i18n.t('buttons.save')"
        @click="updateObservers"
      />
    </template>
  </DeepDialog>
</template>

<script setup lang="ts">
import ObserverInput from '@/components/inputs/ObserverInput.vue';
import { v4 as uuid } from 'uuid';
import {
  observersToDelete,
  observersToUpdate
} from '@/services/documentService';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useEmitter } from '@/composables/useEmitter.ts';
import { useI18n } from 'vue-i18n';
import { cloneDeep } from '@/composables/useUtils';
import { useEmailService } from '@/components/inputs/Emailsuggestion/useEmailService';
import { useRoute } from 'vue-router';
import { DeepDialog } from '@deepcloud/deep-ui-lib';
import type { Observer } from '@/types/Observer';
import { useCreateStore } from '@/stores/create.ts';

const props = defineProps({
  document: {
    type: Object,
    required: true
  }
});

const createStore = useCreateStore();
const showDialog = ref(false);
const observers = ref<Observer[]>();
const observersForm = ref();
const documentObservers = ref<Observer[]>([]);
const inProgress = ref(false);
const isFormValid = ref(true);
const emitter = useEmitter();
const i18n = useI18n();
const route = useRoute();
const { saveRecentlyUsedEmails } = useEmailService();

const isDraftMode = computed(() => route.name === 'document-create');

watch(
  () => observers.value,
  (newValue) => {
    if (newValue) {
      isFormValid.value = newValue.every((obs) => !obs.errorState);
    }
  },
  { deep: true }
);

onMounted(() => {
  emitter.$on('open-observers-dialog', openDialog);
});

onUnmounted(() => {
  emitter.$off('open-observers-dialog', openDialog);
});

function openDialog(newObservers) {
  showDialog.value = true;
  inProgress.value = false;
  observers.value = cloneDeep(newObservers).map((obs) => addKey(obs));
  documentObservers.value = [...newObservers.map((obs) => addKey(obs))];
  if (observers.value && observers.value.length === 0) {
    addNewObserver();
  }
}

function closeDialog() {
  showDialog.value = false;
  observers.value = [];
  inProgress.value = false;
}

function updateObservers() {
  inProgress.value = true;

  if (isDraftMode.value && observers.value) {
    saveRecentlyUsedEmails(observers.value);
  }

  if (observers.value) {
    const toRemove = observersToDelete(
      documentObservers.value,
      observers.value
    );
    const toUpdate = observersToUpdate(
      documentObservers.value,
      observers.value
    );
    const toAdd = observers.value.filter((obs) => obs.observerId === undefined);
    Promise.all([
      Promise.all(
        toRemove.map(async (obs) => {
          await createStore.deleteObserver(props.document!.documentId, obs);
        })
      ),
      Promise.all(
        toUpdate.map((obs) => {
          const observer = observers.value?.find(
            (formObs) => formObs.observerId === obs
          );
          if (observer) {
            const data = {
              email: observer.email?.length === 0 ? '' : observer.email,
              comment: observer.comment ?? '',
              language: observer.language,
              isAdmin: observer.isAdmin
            };
            createStore.updateDocumentObserver(
              props.document!.documentId,
              observer.observerId,
              data
            );
          }
        })
      ),
      Promise.all(
        toAdd.map((obs) => {
          const data = {
            email: obs.email?.length === 0 ? '' : obs.email,
            comment: obs.comment ?? '',
            language: obs.language,
            isAdmin: obs.isAdmin
          };
          createStore.addObserver(props.document!.documentId, data);
        })
      )
    ]).then(() => {
      closeDialog();
    });
  }
}

function updateObserver({ observer, error }) {
  const observerToUpdate = observers.value?.find(
    (obs) => obs.key === observer.key
  );
  if (observerToUpdate) {
    observerToUpdate.email = observer.email;
    observerToUpdate.isAdmin = observer.isAdmin;
    observerToUpdate.errorState = error;
  }

  const emailCounts = {};
  observers.value?.forEach((obs) => {
    if (obs.email) {
      if (obs.email in emailCounts) {
        emailCounts[obs.email]++;
      } else {
        emailCounts[obs.email] = 1;
      }
    }
  });

  if (error || !Object.values(emailCounts).every((count) => count === 1)) {
    isFormValid.value = false;
  } else {
    isFormValid.value = true;
  }
}

// ─── Group members handling ─────────────────────────────────────────
// When ObserverInput emits 'add-group-observers', we receive a list of emails.
// For each email: fill the first empty observer slot, or create a new one.
// Then close the GroupListDialog.

function addGroupObservers(
  emails: string[],
  triggerKey: string,
  adminToggle: boolean
): void {
  if (!observers.value || emails.length === 0) {
    emitter.$emit('close-group-members-dialog');
    return;
  }

  // Remove the triggering row and any other empty rows
  observers.value = observers.value.filter(
    (obs) => obs.key !== triggerKey && !!obs.email
  );

  // Create fresh rows for all emails
  emails.forEach((email) => {
    observers.value!.push({
      key: uuid(),
      email,
      isAdmin: adminToggle ? true : false
    });
  });

  emitter.$emit('close-group-members-dialog');
}

function addNewObserver() {
  const newObserver = {
    key: uuid()
  };
  observers.value?.push(newObserver);
  setTimeout(() => {
    const input = document.getElementById(newObserver.key);
    input?.focus();
  }, 100);
}

function removeObserver(index) {
  observers.value?.splice(index, 1);
  observersForm.value.resetValidation();
  isFormValid.value = observersForm.value.validate();
}

function addKey(object) {
  return {
    key: uuid(),
    ...object
  };
}
</script>

<style scoped lang="scss">
.card-title {
  word-break: break-word;
  font-weight: 600;
  font-size: 24px !important;
  text-align: center;
  margin: 0 0 24px;
  white-space: normal;
}
</style>
